import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Paper
} from '@material-ui/core';
import CommentIcon from '@material-ui/icons/Comment';
import formatDate from '../../utils/formatDate';
import { deleteThread } from '../../actions/threadActions';
import Image from '../common/Image';
import Alert from '../../utils/alert';
import DeleteIcon from '../common/Icons/DeleteIcon';
import noImageUrl from '../../img/no_image_small.jpg';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'stretch',
    background: theme.palette.common.white,
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    marginBottom: theme.spacing(1.5),
    overflow: 'hidden',
    transition: 'box-shadow 0.2s',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
    },
  },
  content: {
    display: 'flex',
    flex: 1,
    textDecoration: 'none',
    color: 'inherit',
    padding: theme.spacing(1),
  },
  image: {
    flex: '0 0 100px',
    marginRight: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imageContent: {
    width: '80px',
    height: '80px',
    objectFit: 'cover',
    borderRadius: '8px',
    background: theme.palette.grey[100],
  },
  flexGrow: {
    flex: 1,
  },
  tools: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
    gap: theme.spacing(0.5),
  },
  toolsActions: {
    marginLeft: theme.spacing(0.5),
    display: 'inline-block',
  },
  customTag: {
    background: theme.palette.warning.main,
    color: theme.palette.warning.dark,
    borderRadius: '4px',
    fontSize: '1rem',
    padding: '0.25em 0.5em',
    fontWeight: 600,
    marginRight: theme.spacing(0.5),
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(0.5),
  },
  userAvatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    objectFit: 'cover',
    background: theme.palette.grey[200],
  },
  info: {
    display: 'flex',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(0.75),
    color: theme.palette.text.secondary,
    alignItems: 'center',
  },
  deleteButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: theme.spacing(1),
    '&:hover': {
      opacity: 0.7,
    },
    '& .MuiSvgIcon-root': {
      fontSize: '2.4rem !important',
      color: '#e53935 !important',
    },
  },
  title: {
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
  commentInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.25),
  },
}));

const ThreadListItem = ({ thread, canDelete }) => {
  const classes = useStyles();
  const currentUser = useSelector(state => state.user.currentUser);
  const dispatch = useDispatch();

  const handleDeleteClick = id => {
    Alert.confirm("この投稿を本当に削除しますか？")
      .then((result) => {
        if (result.value) {
          dispatch(deleteThread(id));
        }
      })
  }

  const renderUserInfo = (user) => (
    <Box className={classes.userInfo}>
      {user.profile.avatar ?
        <Image className={classes.userAvatar} src={user.profile.avatar.image_url} size={'32'} />
        : <img className={classes.userAvatar} src={noImageUrl} alt="no avatar" />
      }
      <Typography variant="body2" component="p">
        <strong>{user.name}</strong>
      </Typography>
    </Box>
  );

  return (
    <Paper className={classes.root} elevation={1}>
      <Link to={`/thread/${thread._id}`} className={classes.content}>
        {thread.mainImage && (
          <Box className={classes.image}>
            <Image className={classes.imageContent} src={thread.mainImage.image_url} size={'80'} />
          </Box>
        )}
        <Box className={classes.flexGrow}>
          {canDelete && currentUser && currentUser._id === thread.user._id && (
            <Box className={classes.tools}>
              <Chip
                label="自分の投稿"
                className={classes.customTag}
                size="small"
              />
              <Box className={classes.toolsActions}>
                <IconButton
                  className={classes.deleteButton}
                  onClick={e => {
                    e.preventDefault();
                    handleDeleteClick(thread._id);
                  }}
                  size="large"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          )}
          <Typography variant="h6" className={classes.title}>
            {thread.title}
          </Typography>
          <Box className={classes.flexGrow}>
            {renderUserInfo(thread.user)}
          </Box>
          <Box className={classes.info}>
            <Typography variant="body2">
              投稿日: <strong>{formatDate(thread.created_at)}</strong>
            </Typography>
            <Box className={classes.commentInfo}>
              <CommentIcon style={{
                color: '#3273dc',
                fontSize: '1.6rem',
              }} />
              <Typography variant="body2">
                x <strong>{thread.comments.length}</strong>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Link>
    </Paper>
  )
}

ThreadListItem.propTypes = {
  thread: PropTypes.object.isRequired
}

export default ThreadListItem

